import { Component, DestroyRef, forwardRef, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    FormArray,
    FormControl,
    FormGroup,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
} from '@angular/forms';
import { ButtonModule } from '@openng/optimus-ui/button';
import { FloatLabelModule } from '@openng/optimus-ui/floatlabel';
import { InputTextModule } from '@openng/optimus-ui/inputtext';

import { CustomControlValueAccessor } from '@app/shared/base/control-value-accessor';

export interface QnAPair {
    question: string;
    answer: string;
}

export interface QnAConfig {
    questionPlaceholder?: string;
    answerPlaceholder?: string;
    addButtonLabel?: string;
}

@Component({
    selector: 'cor-qna-field',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => QnaFieldComponent),
            multi: true,
        },
    ],
    imports: [ReactiveFormsModule, ButtonModule, InputTextModule, FloatLabelModule],
    template: `
        <div class="tw:flex tw:flex-col tw:gap-2 tw:pt-4" role="list">
            @for (group of pairsFormArray.controls; track $index) {
                <div
                    class="tw:flex tw:gap-2 tw:items-center"
                    role="listitem"
                >
                    <p-floatlabel variant="on" class="tw:grow-1">
                        <input
                            pInputText
                            fluid
                            [formControl]="group.controls.question"
                            [placeholder]="qnaConfig()?.questionPlaceholder ?? 'Enter question'"
                            (blur)="onTouched()"
                        />
                        <label class="tw:text-sm! tw:font-normal!">Question {{ $index + 1 }}</label>
                    </p-floatlabel>
                    <p-floatlabel variant="on" class="tw:grow-1">
                        <input
                            pInputText
                            fluid
                            [formControl]="group.controls.answer"
                            [placeholder]="qnaConfig()?.answerPlaceholder ?? 'Enter answer'"
                            (blur)="onTouched()"
                        />
                        <label class="tw:text-sm! tw:font-normal!">Answer {{ $index + 1 }}</label>
                    </p-floatlabel>
                    @if (!pairsFormArray.disabled) {
                        <p-button
                            icon="pi pi-trash"
                            [rounded]="true"
                            [text]="true"
                            severity="danger"
                            size="small"
                            (onClick)="removePair($index)"
                        />
                    }
                </div>
            }
            @if (!pairsFormArray.disabled) {
                <p-button
                    [label]="qnaConfig()?.addButtonLabel ?? 'Add Question'"
                    icon="pi pi-plus"
                    [outlined]="true"
                    size="small"
                    (onClick)="addPair()"
                />
            }
        </div>
    `,
})
export class QnaFieldComponent extends CustomControlValueAccessor<string> {
    readonly qnaConfig = input<QnAConfig>();

    readonly pairsFormArray = new FormArray<
        FormGroup<{ question: FormControl<string>; answer: FormControl<string> }>
    >([]);

    private readonly destroyRef = inject(DestroyRef);

    constructor() {
        super();
        this.pairsFormArray.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.emitValue());
    }

    private parseValue(value: string | null): QnAPair[] {
        let pairs: QnAPair[] = [];
        try {
            pairs = JSON.parse(value || '[]') as QnAPair[];
        } catch (error) {
            console.error(`Failed to parse QnAPairs: ${value}`, error);
        }
        return pairs.length > 0 ? pairs : [{ question: '', answer: '' }];
    }

    private createPairGroup(pair: QnAPair): FormGroup<{
        question: FormControl<string>;
        answer: FormControl<string>;
    }> {
        return new FormGroup({
            question: new FormControl(pair.question, { nonNullable: true }),
            answer: new FormControl(pair.answer, { nonNullable: true }),
        });
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.pairsFormArray.disable({ emitEvent: false });
        } else {
            this.pairsFormArray.enable({ emitEvent: false });
        }
    }

    writeValue(value: string | null): void {
        const pairs = this.parseValue(value);
        const current = this.pairsFormArray.controls.length;
        const target = pairs.length || 1;

        if (target > current) {
            for (let i = current; i < target; i++) {
                this.pairsFormArray.push(
                    this.createPairGroup(pairs[i] ?? { question: '', answer: '' }),
                    { emitEvent: false },
                );
            }
        } else if (target < current) {
            for (let i = current - 1; i >= target; i--) {
                this.pairsFormArray.removeAt(i, { emitEvent: false });
            }
        }

        this.pairsFormArray.patchValue(pairs.length > 0 ? pairs : [{ question: '', answer: '' }], {
            emitEvent: false,
        });
    }

    addPair(): void {
        this.pairsFormArray.push(this.createPairGroup({ question: '', answer: '' }));
        this.emitValue();
    }

    removePair(index: number): void {
        this.pairsFormArray.removeAt(index);
        if (this.pairsFormArray.length === 0) {
            this.pairsFormArray.push(this.createPairGroup({ question: '', answer: '' }));
        }
        this.emitValue();
    }

    private emitValue(): void {
        const value: QnAPair[] = this.pairsFormArray.getRawValue();
        this.onChange(JSON.stringify(value));
    }
}
